import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { BehaviorSubject, Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { SocialLink } from '@personal-area/global/social-authorization/models';
import { environment } from '@env/environment';

const URLs = {
  socialMediaResources: `/assets/cfg/social-networks.json`
};

export type SocialMediaResourcesResponse = { [language: string]: SocialLink[] }

@Injectable({ providedIn: 'root' })
export class SocialService {
  private _cache = new BehaviorSubject<SocialMediaResourcesResponse>({});

  constructor(private _http: HttpClient) {
  }

  public fetch = (lang: string): Observable<SocialLink[]> => {
    if (this._cache.value[lang]) {
      return this._cache.pipe(map(cache => cache[lang]));
    }

    return this._http.get<SocialMediaResourcesResponse>(URLs.socialMediaResources).pipe(
      map(response => this._composeSocialMediaLinks(response, lang)),
      tap(links => this._saveToCache(links, lang))
    );
  }

  private _composeSocialMediaLinks = (response: SocialMediaResourcesResponse, selectedLanguage: string): SocialLink[] => {
    if (!response || !(selectedLanguage in response) || !response[selectedLanguage]) {
      return [];
    }

    const availableResources = response[selectedLanguage].filter(({ name }) => environment.availableSocials.includes(name));

    return this._removeDuplicatedResources(availableResources);
  }

  private _removeDuplicatedResources = (resources: SocialLink[]): SocialLink[] => resources
    .reduce((acc: SocialLink[], cur: SocialLink) => {
      return acc.some(x => x.name === cur.name) ? acc : [...(acc || []), cur];
    }, [])

  private _saveToCache = (links: SocialLink[], lang: string) => {
    this._cache.next({
      ...this._cache.value,
      [lang]: links
    })
  }
}
